"use client"

import {
  InfiniteTable,
  type TableItem,
  TableSkeleton,
} from "@/app/components/infinite-table/infinite-table"
import { HeaderRow } from "@/app/components/table"
import { Cell } from "@/app/profiles/bids/table/cells"
import type { BidItem } from "@anaconda/core/bid/types"
import { reatomComponent } from "@reatom/react"
import { BIDS_COLUMNS as COLUMNS } from "./bids-columns"

const COMPONENTS = {
  HeaderRow: BidsHeaderRow,
  RowContent,
  RowSkeleton,
} as const

const getItemKey = (item: BidItem) => item.bid.id

interface Props {
  items: TableItem<BidItem>[]
  loadNext: () => void
  emptyState: React.ReactNode
}

/**
 * Shared table for the collection Offers sub-tabs. Renders item bids and
 * collection bids side by side; see bids-columns.tsx for why it does not reuse
 * the profile bids columns verbatim.
 */
export const BidsTableReatom = reatomComponent<Props>(
  ({ items, loadNext, emptyState }) => {
    if (items.length === 0) {
      return (
        <div className="py-10 text-center text-foreground/60">{emptyState}</div>
      )
    }

    return (
      <InfiniteTable
        getItemKey={getItemKey}
        items={items}
        loadNext={loadNext}
        components={COMPONENTS}
        initialItemCount={15}
      />
    )
  },
  "BidsTable",
)

export function BidsTableSkeleton() {
  return <TableSkeleton HeaderRow={BidsHeaderRow} RowSkeleton={RowSkeleton} />
}

function BidsHeaderRow() {
  return (
    <HeaderRow>
      {COLUMNS.map((column, index) => (
        <column.Header key={index} />
      ))}
    </HeaderRow>
  )
}

function RowContent({ content }: { content: BidItem }) {
  return (
    <>
      {COLUMNS.map((column, index) => (
        <Cell key={index}>
          <column.Content {...content} />
        </Cell>
      ))}
    </>
  )
}

function RowSkeleton() {
  return (
    <>
      {COLUMNS.map((column, index) => (
        <Cell key={index}>
          <column.Skeleton />
        </Cell>
      ))}
    </>
  )
}
